import React, { useEffect, useState, useCallback, useContext } from "react";
import {
  Paper,
  makeStyles,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  MenuItem,
  Button,
  Chip,
  CircularProgress
} from "@material-ui/core";
import { toast } from "react-toastify";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(3),
    overflowY: "auto",
    margin: theme.spacing(2)
  },
  filters: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    marginBottom: theme.spacing(2)
  },
  field: { minWidth: 160 },
  diff: {
    fontFamily: "monospace",
    fontSize: 12,
    whiteSpace: "pre-wrap",
    wordBreak: "break-all",
    maxWidth: 360
  },
  pagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: theme.spacing(2)
  }
}));

const ACTIONS = ["", "login", "create", "update", "delete"];

const AuditLogs = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.profile === "admin" || user?.profile === "owner";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    entityType: "",
    action: "",
    userId: "",
    clientId: ""
  });

  const fetchData = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const params = { page, pageSize };
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params[k] = v;
      });
      const { data } = await api.get("/audit-logs", { params });
      setRows(data.data || []);
      setTotal(data.pagination?.total || 0);
    } catch (err) {
      toast.error("Erro ao carregar logs de auditoria");
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize, isAdmin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!isAdmin) {
    return (
      <Paper className={classes.mainPaper}>
        <Typography variant="h6">Acesso negado</Typography>
        <Typography color="textSecondary">
          Você não tem permissão para acessar os logs de auditoria.
        </Typography>
      </Paper>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <Paper className={classes.mainPaper}>
      <Typography variant="h5" gutterBottom>
        Logs de auditoria
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Histórico de ações realizadas no sistema.
      </Typography>

      <div className={classes.filters}>
        <TextField
          label="Tipo de entidade"
          className={classes.field}
          value={filters.entityType}
          onChange={e =>
            setFilters(f => ({ ...f, entityType: e.target.value }))
          }
          placeholder="User, Client, Deal..."
        />
        <TextField
          select
          label="Ação"
          className={classes.field}
          value={filters.action}
          onChange={e => setFilters(f => ({ ...f, action: e.target.value }))}
        >
          {ACTIONS.map(a => (
            <MenuItem key={a || "all"} value={a}>
              {a || "Todas"}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Usuário (id)"
          className={classes.field}
          value={filters.userId}
          onChange={e => setFilters(f => ({ ...f, userId: e.target.value }))}
        />
        <TextField
          label="Cliente (id)"
          className={classes.field}
          value={filters.clientId}
          onChange={e => setFilters(f => ({ ...f, clientId: e.target.value }))}
        />
        <Button
          variant="outlined"
          onClick={() => {
            setPage(1);
            fetchData();
          }}
        >
          Filtrar
        </Button>
      </div>

      {loading ? (
        <CircularProgress />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell>Usuário</TableCell>
              <TableCell>Entidade</TableCell>
              <TableCell>Ação</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell>IP</TableCell>
              <TableCell>Diff</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.id}>
                <TableCell>
                  {new Date(r.createdAt).toLocaleString("pt-BR")}
                </TableCell>
                <TableCell>
                  {r.user ? `${r.user.name} (#${r.user.id})` : "—"}
                </TableCell>
                <TableCell>
                  {r.entityType}
                  {r.entityId ? ` #${r.entityId}` : ""}
                </TableCell>
                <TableCell>
                  <Chip size="small" label={r.action} />
                </TableCell>
                <TableCell>
                  {r.client ? `${r.client.name} (#${r.client.id})` : "—"}
                </TableCell>
                <TableCell>{r.ip || "—"}</TableCell>
                <TableCell className={classes.diff}>
                  {r.diff ? JSON.stringify(r.diff, null, 2) : "—"}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Nenhum registro encontrado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <div className={classes.pagination}>
        <Typography variant="body2" color="textSecondary">
          Total: {total} • Página {page} de {totalPages}
        </Typography>
        <div>
          <Button
            disabled={page <= 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
          >
            Anterior
          </Button>
          <Button
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            Próxima
          </Button>
        </div>
      </div>
    </Paper>
  );
};

export default AuditLogs;
